<template>
  <touch-layout>
    <template #header_page_breadcrumb>
      <Breadcrumbs
        :routes="[
          ['Dashboard', '/'],
          ['Enquiries', '/enquiries'],
        ]"
      />
    </template>
    <template #header_page_title>
      <div>
        Enquiries
        <info-popup
          :id="'enquiries:index'"
          :info_message="'As customers make an enquiry via your website designer they will land here, click an enquiry for more details of the customers request.'"
        />
      </div>
    </template>
    <Teleport defer to="#portal_search">
      <SearchBox class="flex" placeholder="Search for enquiry..." />
    </Teleport>
    <loading
      :loading="loading"
      class="bg-gray-200 w-full h-full overflow-y-auto scrolling-touch p-3 md:p-10"
    >
      <animation-staggered-fade appear name="fade-in">
        <div key="quotes_list" class="bg-white p-3 md:p-10 pb-6 border" data-index="1">
          <table class="table-lined xs:table-unstacked _with-hover w-full">
            <thead>
              <tr>
                <th class="w-10">#</th>
                <th class="w-16">Source</th>
                <th>Date</th>
                <th class="xs:hidden lg:table-cell">Name</th>
                <th>Postcode</th>
                <th>Email</th>
                <th class="xs:hidden lg:table-cell">Phone</th>
                <th>
                  <FilterDropdown
                    label="Status"
                    query-label="statusFilter"
                    :filters="statuses_dropdown"
                  />
                </th>
                <th class="w-16">
                  <button class="btn-action" @click.stop="loadFeed">Reload</button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="quote in quotes"
                :key="quote.id"
                :class="{ _hovered: hovered === quote.id }"
                @mouseover="hovered = quote.id"
                @mouseleave="hovered = null"
                @click="$router.push(quote.url)"
              >
                <td>
                  <div class="td-title w-24">Number:</div>
                  <div class="td-content">{{ quote.customerCounter }}</div>
                </td>
                <td>
                  <div class="td-title w-24">Source:</div>
                  <div
                    :title="quote.fromDealer ? 'Dealer Network' : 'Branded Mode'"
                  >
                    <img
                      v-if="quote.fromDealer"
                      src="/images/dealer-network-icon.png"
                      class="w-8 m-auto"
                    >
                    <img
                      v-else
                      :src="store_url + 'images/icons/Br.png'"
                      class="w-8 m-auto"
                    >
                  </div>
                </td>
                <td>
                  <div class="td-title w-24">Date:</div>
                  <div class="td-content">{{ formatDate(quote.date) }}</div>
                </td>
                <template v-if="quote.consumer">
                  <td class="xs:hidden lg:table-cell">
                    <div class="td-title w-24">Name:</div>
                    <div class="td-content">
                      {{ quote.consumer.firstName }} {{ quote.consumer.lastName }}
                    </div>
                  </td>
                  <td>
                    <div class="td-title w-24">Postcode:</div>
                    <div class="td-content">{{ quote.consumer.addressCode }}</div>
                  </td>
                  <td>
                    <div class="td-title w-24">Email:</div>
                    <div class="td-content truncate xs:w-24 md:w-auto">
                      {{ quote.consumer.email }}
                    </div>
                  </td>
                  <td class="xs:hidden lg:table-cell">
                    <div class="td-title w-24">Phone:</div>
                    <div class="td-content">{{ quote.consumer.mobile }}</div>
                  </td>
                </template>
                <template v-else>
                  <td class="xs:hidden lg:table-cell">
                    <div class="td-title w-24">Name:</div>
                    <div class="td-content">-</div>
                  </td>
                  <td>
                    <div class="td-title w-24">Postcode:</div>
                    <div class="td-content">-</div>
                  </td>
                  <td>
                    <div class="td-title w-24">Email:</div>
                    <div class="td-content">-</div>
                  </td>
                  <td class="xs:hidden lg:table-cell">
                    <div class="td-title w-24">Phone:</div>
                    <div class="td-content">-</div>
                  </td>
                </template>
                <td>
                  <div class="td-title w-24">Status:</div>
                  <div class="td-content whitespace-nowrap truncate xs:w-24 md:w-auto">
                    <div
                      class="inline-block rounded-full w-2 h-2 mr-2"
                      :class="statusClass(quote.enquiryStatus)"
                    />
                    <span v-text="formatStatus(quote.enquiryStatus)" />
                  </div>
                </td>
                <td class="td-action-buttons">
                  <div class="table-edit-btn">
                    <i class="fa fa-pencil" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="quotes.length === 0" key="none-found" class="bg-white p-3 xs:p-10">
            <div class="flex justify-between">
              <div>No Enquiries to show</div>
              <div v-if="$route.query.search">
                <a class="btn-action" @click.stop="$router.push({ query: undefined }), loadFeed()">
                  Clear Search
                </a>
              </div>
            </div>
          </div>
        </div>
        <pagination :total_items="total_items" :limit="limit" class="mt-3 pb-10" @load-feed="loadFeed" />
      </animation-staggered-fade>
    </loading>
  </touch-layout>
</template>

<script>
import { mapStores } from 'pinia'
import { useTouchStore } from '@/pinia/touch';
import { formatDate } from '@/composables/date';
import Pagination from '@/components/shared/Pagination.vue';
import Breadcrumbs from '@/components/shared/Breadcrumbs.vue';
import SearchBox from '@/components/shared/SearchBox.vue';
import FilterDropdown from '@/components/shared/FilterDropdown.vue';

export default {
  components: {
    Breadcrumbs,
    pagination: Pagination,
    SearchBox,
    FilterDropdown
  },
  setup() {
    return { formatDate }
  },
  data() {
    return {
      quotes: [],
      loading: true,
      limit: 10,
      total_items: 0,
      statuses: [],
      hovered: null,
      isDelay: true,
    };
  },
  computed: {
    ...mapStores(useTouchStore),
    statuses_dropdown() {
      return this.statuses.filter(x => x.value !== 0).map(y => ({
        id: y.value,
        name: y.description,
        dot: this.statusClass(y.value)
      }))
    }
  },
  watch: {
    '$route.query': {
      handler() {
        this.loadFeed();
      },
      immediate: true,
    },
  },
  async mounted() {
    const statuses = await this.touchStore.getEnquiryStatuses();
    if (statuses) {
      this.statuses = statuses.filter(x => !['Refused', 'Dead', 'Expired'].includes(x.name))
    }
  },
  methods: {
    formatStatus(statusId) {
      const matchingStatus = this.statuses.filter((status) => status.value === statusId)[0];
      return matchingStatus ? matchingStatus.description : 'Not Found';
    },
    statusClass(statusId) {
      switch (statusId) {
        case 0:
          return 'bg-gray-300';
        case 1:
          return 'bg-green-600';
        case 2:
          return 'bg-touch-purple';
        case 3:
          return 'bg-touch-orange';
        case 4:
          return 'bg-touch-red';
        default:
          return 'bg-white border';
      }
    },
    delaySearch() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.loadFeed();
      }, 300);
    },
    async loadFeed(offset = 0) {
      this.loading = true;
      const response = await this.touchStore.loadEnquiries({
        limit: this.limit,
        offset,
        statusId: this.$route.query.statusFilter || 0,
        search: this.$route.query.search,
      });
      this.quotes = response.enquiries;
      this.total_items = response.total_items;
      this.loading = false;
    },
  },
};
</script>
